/**
 * Typed API client generated from the backend OpenAPI document (`npm run gen:api`).
 *
 * Security model in the browser:
 * - the access token lives only in memory (never localStorage);
 * - the refresh token is an httpOnly, SameSite=Strict cookie the script cannot read;
 * - on 401 the client refreshes once and retries the request.
 */
import createClient, { type Middleware } from "openapi-fetch";

import type { components, paths } from "./schema";

export type Schemas = components["schemas"];
export type Me = Schemas["MeOut"];

let accessToken: string | null = null;
let actingCompanyId: string | null = null;
let onSessionLost: () => void = () => {};
let refreshing: Promise<Schemas["TokenOut"] | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
/** Current access token (for the live WebSocket, which authenticates with its first message). */
export function getAccessToken(): string | null {
  return accessToken;
}
export function getActingCompany(): string | null {
  return actingCompanyId;
}
export function setActingCompany(id: string | null): void {
  actingCompanyId = id;
}
export function setSessionLostHandler(fn: () => void): void {
  onSessionLost = fn;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

/** Single-flight refresh using the httpOnly cookie. */
export function refreshSession(): Promise<Schemas["TokenOut"] | null> {
  refreshing ??= fetch("/api/v1/auth/refresh", { method: "POST", credentials: "same-origin" })
    .then(async (r) => {
      if (!r.ok) return null;
      const body = (await r.json()) as Schemas["TokenOut"];
      accessToken = body.access_token;
      return body;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

const pending = new WeakMap<Request, Request>();

const auth: Middleware = {
  onRequest({ request }) {
    if (accessToken) request.headers.set("Authorization", `Bearer ${accessToken}`);
    if (actingCompanyId) request.headers.set("X-Company-Id", actingCompanyId);
    pending.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401 || request.url.includes("/api/v1/auth/")) return response;
    const retry = pending.get(request);
    const refreshed = await refreshSession();
    if (!refreshed || !retry) {
      onSessionLost();
      return response;
    }
    retry.headers.set("Authorization", `Bearer ${refreshed.access_token}`);
    return fetch(retry);
  },
};

// Absolute same-origin base URL (the API is served behind the same origin as the SPA).
export const api = createClient<paths>({
  baseUrl: window.location.origin,
  credentials: "same-origin",
  // Resolve fetch at call time (keeps instrumentation, polyfills and test doubles effective).
  fetch: (request) => globalThis.fetch(request),
});
api.use(auth);

type ErrorBody = { error?: { code?: string; message?: string; details?: unknown } };

/** Unwrap an openapi-fetch result or throw an ApiError with the server's message. */
export async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const { data, error, response } = await p;
  if (error !== undefined || !response.ok) {
    const e = (error ?? {}) as ErrorBody;
    throw new ApiError(
      response.status,
      e.error?.code ?? "error",
      e.error?.message ?? `Request failed (${response.status})`,
      e.error?.details,
    );
  }
  return data as T;
}

export function hasPermission(me: Me | null | undefined, permission: string): boolean {
  return !!me && me.permissions.includes(permission);
}
